'use client';

import * as React from 'react';
import { ChevronDown, ChevronUp, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';

export type ContractParagraph = {
    text: string;
    style?: 'body' | 'muted-small';
    gapAfter?: number;
};

export type ContractClause = {
    id: string;
    title: string;
    paragraphs: ContractParagraph[];
};

export type ContractClauseTemplate = {
    introParagraphs: ContractParagraph[];
    clauses: ContractClause[];
    feeScheduleAfterClauseId: string;
};

export const PARTNER_NAME_PLACEHOLDER = '{{partnerName}}';

function newClauseId(title: string): string {
    const base = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return base ? `${base}-${Date.now().toString(36)}` : `clause-${Date.now().toString(36)}`;
}

interface ClauseEditorProps {
    template: ContractClauseTemplate;
    onChange: (template: ContractClauseTemplate) => void;
    onReset?: () => void;
    showIntro?: boolean;
}

export function ClauseEditor({ template, onChange, onReset, showIntro = true }: ClauseEditorProps) {
    const updateClause = (index: number, patch: Partial<ContractClause>) => {
        const clauses = template.clauses.map((clause, i) =>
            i === index ? { ...clause, ...patch } : clause,
        );
        onChange({ ...template, clauses });
    };

    const updateParagraph = (clauseIndex: number, paragraphIndex: number, text: string) => {
        const clauses = [...template.clauses];
        const paragraphs = [...clauses[clauseIndex].paragraphs];
        paragraphs[paragraphIndex] = { ...paragraphs[paragraphIndex], text };
        clauses[clauseIndex] = { ...clauses[clauseIndex], paragraphs };
        onChange({ ...template, clauses });
    };

    const addParagraph = (clauseIndex: number) => {
        const clauses = [...template.clauses];
        clauses[clauseIndex] = {
            ...clauses[clauseIndex],
            paragraphs: [...clauses[clauseIndex].paragraphs, { text: '' }],
        };
        onChange({ ...template, clauses });
    };

    const removeParagraph = (clauseIndex: number, paragraphIndex: number) => {
        const clauses = [...template.clauses];
        const paragraphs = clauses[clauseIndex].paragraphs.filter((_, i) => i !== paragraphIndex);
        if (paragraphs.length === 0) return;
        clauses[clauseIndex] = { ...clauses[clauseIndex], paragraphs };
        onChange({ ...template, clauses });
    };

    const addClause = () => {
        const title = 'New clause';
        const id = newClauseId(title);
        onChange({
            ...template,
            clauses: [
                ...template.clauses,
                {
                    id,
                    title,
                    paragraphs: [{ text: `Use ${PARTNER_NAME_PLACEHOLDER} for the partner name.` }],
                },
            ],
        });
    };

    const removeClause = (index: number) => {
        if (template.clauses.length <= 1) return;
        const removed = template.clauses[index];
        const clauses = template.clauses.filter((_, i) => i !== index);
        const feeScheduleAfterClauseId =
            template.feeScheduleAfterClauseId === removed.id
                ? clauses[0]?.id ?? 'nature-of-agreement'
                : template.feeScheduleAfterClauseId;
        onChange({ ...template, clauses, feeScheduleAfterClauseId });
    };

    const moveClause = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= template.clauses.length) return;
        const clauses = [...template.clauses];
        [clauses[index], clauses[target]] = [clauses[target], clauses[index]];
        onChange({ ...template, clauses });
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                    Use <code className="rounded bg-muted px-1">{PARTNER_NAME_PLACEHOLDER}</code> where the
                    partner name should appear. The fee grid is inserted after the clause marked below.
                </p>
                {onReset && (
                    <Button type="button" variant="outline" size="sm" onClick={onReset}>
                        <RotateCcw className="mr-2 h-3.5 w-3.5" />
                        Reset to default
                    </Button>
                )}
            </div>

            {showIntro && (
                <div className="space-y-2 rounded-lg border p-3">
                    <Label>Introduction</Label>
                    <Textarea
                        rows={3}
                        value={template.introParagraphs[0]?.text ?? ''}
                        onChange={(e) =>
                            onChange({
                                ...template,
                                introParagraphs: [{ text: e.target.value, gapAfter: 12 }],
                            })
                        }
                    />
                </div>
            )}

            <div className="space-y-2">
                <Label>Fee schedule position</Label>
                <Select
                    value={template.feeScheduleAfterClauseId}
                    onValueChange={(feeScheduleAfterClauseId) =>
                        onChange({ ...template, feeScheduleAfterClauseId })
                    }
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Insert fee tables after…" />
                    </SelectTrigger>
                    <SelectContent>
                        {template.clauses.map((clause) => (
                            <SelectItem key={clause.id} value={clause.id}>
                                After “{clause.title}”
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-3">
                {template.clauses.map((clause, index) => (
                    <Collapsible key={clause.id} defaultOpen={index < 2}>
                        <div className="rounded-lg border">
                            <div className="flex items-start gap-2 border-b px-3 py-2">
                                <CollapsibleTrigger asChild>
                                    <Button type="button" variant="ghost" size="sm" className="mt-1 px-2">
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </CollapsibleTrigger>
                                <div className="min-w-0 flex-1 space-y-2 py-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Input
                                            value={clause.title}
                                            onChange={(e) =>
                                                updateClause(index, { title: e.target.value })
                                            }
                                            placeholder="Section title"
                                        />
                                        {clause.id === template.feeScheduleAfterClauseId && (
                                            <Badge variant="secondary">Fee schedule follows</Badge>
                                        )}
                                    </div>
                                </div>
                                <div className="flex shrink-0 gap-1 pt-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        disabled={index === 0}
                                        onClick={() => moveClause(index, -1)}
                                    >
                                        <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        disabled={index === template.clauses.length - 1}
                                        onClick={() => moveClause(index, 1)}
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        disabled={template.clauses.length <= 1}
                                        onClick={() => removeClause(index)}
                                    >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </div>
                            <CollapsibleContent className="space-y-3 px-3 py-3">
                                {clause.paragraphs.map((paragraph, paragraphIndex) => (
                                    <div key={paragraphIndex} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs text-muted-foreground">
                                                Paragraph {paragraphIndex + 1}
                                            </Label>
                                            {clause.paragraphs.length > 1 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeParagraph(index, paragraphIndex)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                        <Textarea
                                            rows={4}
                                            value={paragraph.text}
                                            onChange={(e) =>
                                                updateParagraph(index, paragraphIndex, e.target.value)
                                            }
                                        />
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addParagraph(index)}
                                >
                                    Add paragraph
                                </Button>
                            </CollapsibleContent>
                        </div>
                    </Collapsible>
                ))}
            </div>

            <Button type="button" variant="outline" onClick={addClause}>
                <Plus className="mr-2 h-4 w-4" />
                Add clause
            </Button>
        </div>
    );
}
